import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { seedWorkspace } from '../data/seedWorkspace';
import { fetchPublicWorkspace } from '../services/workspaceApi';
import type { MenuCategory, MenuItem, RestaurantSettings, RestaurantWorkspace } from '../types/workspace';
import { normalizeWorkspacePayload } from '../utils/workspaceHelpers';

type PublicStatus = 'loading' | 'ready' | 'error';

interface SiteWorkspaceContextValue {
  workspace: RestaurantWorkspace;
  settings: RestaurantSettings;
  categories: MenuCategory[];
  items: MenuItem[];
  status: PublicStatus;
  publishedAt?: string;
  source: 'remote' | 'seed';
  error: string | null;
  refresh: () => Promise<void>;
  getCategoryById: (id: string) => MenuCategory | undefined;
  getItemById: (id: string) => MenuItem | undefined;
  getItemsByCategory: (categoryId: string) => MenuItem[];
}

const SiteWorkspaceContext = createContext<SiteWorkspaceContextValue | undefined>(undefined);

const sortByOrder = <T extends { sortOrder: number }>(items: T[]) =>
  [...items].sort((left, right) => left.sortOrder - right.sortOrder);

export const SiteWorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [workspace, setWorkspace] = useState<RestaurantWorkspace>(normalizeWorkspacePayload(seedWorkspace));
  const [status, setStatus] = useState<PublicStatus>('loading');
  const [publishedAt, setPublishedAt] = useState<string | undefined>();
  const [source, setSource] = useState<'remote' | 'seed'>('seed');
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await fetchPublicWorkspace();
      setWorkspace(normalizeWorkspacePayload(response.workspace));
      setPublishedAt(response.publishedAt);
      setSource(response.source);
      setError(null);
      setStatus('ready');
    } catch (caughtError) {
      setWorkspace(normalizeWorkspacePayload(seedWorkspace));
      setPublishedAt(undefined);
      setSource('seed');
      setError(caughtError instanceof Error ? caughtError.message : 'Could not load published menu');
      setStatus('ready');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const categories = useMemo(
    () => sortByOrder(workspace.categories.filter((category) => category.isVisible)),
    [workspace.categories],
  );

  const items = useMemo(
    () => sortByOrder(workspace.items.filter((item) => item.isVisible)),
    [workspace.items],
  );

  const getCategoryById = useCallback(
    (id: string) => workspace.categories.find((category) => category.id === id),
    [workspace.categories],
  );

  const getItemById = useCallback(
    (id: string) => workspace.items.find((item) => item.id === id),
    [workspace.items],
  );

  const getItemsByCategory = useCallback(
    (categoryId: string) =>
      sortByOrder(
        workspace.items.filter(
          (item) => item.categoryId === categoryId && item.isVisible && item.isAvailable,
        ),
      ),
    [workspace.items],
  );

  const value = useMemo<SiteWorkspaceContextValue>(
    () => ({
      workspace,
      settings: workspace.settings,
      categories,
      items,
      status,
      publishedAt,
      source,
      error,
      refresh,
      getCategoryById,
      getItemById,
      getItemsByCategory,
    }),
    [categories, error, getCategoryById, getItemById, getItemsByCategory, items, publishedAt, refresh, source, status, workspace],
  );

  return <SiteWorkspaceContext.Provider value={value}>{children}</SiteWorkspaceContext.Provider>;
};

export const useSiteWorkspace = () => {
  const context = useContext(SiteWorkspaceContext);

  if (!context) {
    throw new Error('useSiteWorkspace must be used within SiteWorkspaceProvider');
  }

  return context;
};
